// opervex-frontend/src/components/DynamicTable/index.js

import React, { useState } from "react";
import { Table, Pagination } from "react-bootstrap";

import CustomTableRow from "./CustomTableRow";

const DynamicTable = ({
  layout = [],
  dados = [],
  page = 0,
  rowsPerPage = 10,
  setPage,
  setRowsPerPage,
  links,
  noPagination = false,
}) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  // Garante que dados seja sempre um array
  const safeDados = Array.isArray(dados) ? dados : [];

  /**
   * Altera a ordenação da coluna
   */
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  /**
   * Converte um valor de data para timestamp de forma segura.
   */
  const getDateValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    // Data sem horário: YYYY-MM-DD
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);

      return new Date(year, month - 1, day).getTime();
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return null;
    }

    return date.getTime();
  };

  /**
   * Ordena os dados conforme o tipo definido no layout.
   */
  const sortData = (array, property, direction) => {
    if (!property) {
      return array;
    }

    const column = layout.find((col) => col.key === property);

    return [...array].sort((a, b) => {
      let aValue = a?.[property];
      let bValue = b?.[property];

      const aEmpty = aValue === null || aValue === undefined || aValue === "";

      const bEmpty = bValue === null || bValue === undefined || bValue === "";

      // Valores vazios ficam sempre por último
      if (aEmpty && bEmpty) {
        return 0;
      }

      if (aEmpty) {
        return 1;
      }

      if (bEmpty) {
        return -1;
      }

      /**
       * DATA / DATA E HORA
       */
      if (column?.type === "date" || column?.type === "datetime") {
        aValue = getDateValue(aValue);
        bValue = getDateValue(bValue);

        if (aValue === null && bValue === null) {
          return 0;
        }

        if (aValue === null) {
          return 1;
        }

        if (bValue === null) {
          return -1;
        }
      } else if (column?.type === "number" || column?.type === "currency" || column?.type === "decimal") {

      /**
       * NÚMEROS
       */
        const aNumber = Number(aValue);
        const bNumber = Number(bValue);

        aValue = isNaN(aNumber) ? 0 : aNumber;
        bValue = isNaN(bNumber) ? 0 : bNumber;
      } else {

      /**
       * TEXTO
       */
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (aValue < bValue) {
        return direction === "asc" ? -1 : 1;
      }

      if (aValue > bValue) {
        return direction === "asc" ? 1 : -1;
      }

      return 0;
    });
  };

  const sortedData = sortData(safeDados, orderBy, order);

  /**
   * Alteração de página
   */
  const handleChangePage = (newPage) => {
    if (!setPage) {
      return;
    }

    setPage(newPage);
  };

  /**
   * Alteração da quantidade de registros por página
   */
  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);

    if (setRowsPerPage) {
      setRowsPerPage(value);
    }

    if (setPage) {
      setPage(0);
    }
  };

  /**
   * Total de páginas
   */
  const totalPages = rowsPerPage > 0 ? Math.ceil(safeDados.length / rowsPerPage) : 0;

  /**
   * Define intervalo da paginação
   */
  let startPage = Math.max(0, page - 3);

  let endPage = Math.min(totalPages - 1, startPage + 6);

  if (endPage - startPage < 6) {
    startPage = Math.max(0, endPage - 6);
  }

  /**
   * Dados que serão exibidos
   */
  const visibleData = noPagination ? sortedData : sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  /**
   * Menu de paginação
   */
  const PaginationMenu = () => {
    if (noPagination || totalPages <= 1) {
      return null;
    }

    return (
      <Pagination>
        <Pagination.First onClick={() => handleChangePage(0)} disabled={page === 0} />

        <Pagination.Prev onClick={() => handleChangePage(Math.max(0, page - 1))} disabled={page === 0} />

        {Array.from(
          {
            length: Math.max(0, endPage - startPage + 1),
          },
          (_, index) => startPage + index,
        ).map((p) => (
          <Pagination.Item key={p} active={p === page} onClick={() => handleChangePage(p)}>
            {p + 1}
          </Pagination.Item>
        ))}

        <Pagination.Next onClick={() => handleChangePage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} />

        <Pagination.Last onClick={() => handleChangePage(totalPages - 1)} disabled={page >= totalPages - 1} />
      </Pagination>
    );
  };

  return (
    <div>
      <Table striped bordered hover size="sm">
        <thead>
          <tr className="text-center align-middle">
            {layout.map(
              (col) =>
                col.visible && (
                  <th
                    key={col.key}
                    onClick={() => handleRequestSort(col.key)}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    {col.label}

                    {orderBy === col.key && <> {order === "asc" ? "▲" : "▼"}</>}
                  </th>
                ),
            )}
          </tr>
        </thead>

        <tbody>
          {visibleData.length > 0 ? (
            visibleData.map((row, index) =>
              CustomTableRow({
                row,
                layout,
                links,
                index: noPagination ? index : page * rowsPerPage + index,
              }),
            )
          ) : (
            <tr>
              <td colSpan={layout.filter((col) => col.visible).length || 1} className="text-center">
                Nenhum registro encontrado.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {!noPagination && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <select onChange={handleChangeRowsPerPage} value={rowsPerPage} className="form-select w-auto">
            {[5, 10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} por página
              </option>
            ))}
          </select>

          <PaginationMenu />
        </div>
      )}
    </div>
  );
};

export default DynamicTable;
